import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Box, Typography, IconButton } from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import { MzTabContext, MzTabPanel } from "@markaaz/ui/MzTabControls";
import MzButton from "@markaaz/ui/MzButton";
import I18n from "i18next";

import {
  selectMatchConfidenceValues,
  loadMatchConfidenceValues,
  submitMatchConfidenceValues,
  updateMatchConfidenceValues
} from "store/slices/SystemSettingsPage";
import { loadApiKeys } from "store/slices/administrationPage";
import { selectApiKey } from "store/slices/administrationPage";
import AdministrationMain from "components/systemLevelSettings/authenticationEndpoint/AdministrationMain";
import matchConfidenceData from "../data/matchConfidence-data.json";
import MatchConfidenceMain from "components/systemLevelSettings/matchConfidence/MatchConfidenceMain";
import { loadingPage, selectUser } from "store/slices/app";
import ContractTermsPage from "components/systemLevelSettings/contractTerms/ContractTermsPage";

const sx = {
  heading: {
    color: (theme) => theme.palette.darkBlue.c100
  },
  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  }
};

const AccessAdministration = () => {
  const { tab } = useParams();
  const userAPIKeyValues = useSelector(selectApiKey);
  const dispatch = useDispatch();

  const dataList = [
    {
      label: "API Key",
      value: userAPIKeyValues?.APIKey,
      desc: "API Key for either the production or sandbox depending on the environment view selected in the page header.",
      icon: (
        <IconButton color="primary" sx={sx.icon} onClick={() => navigator.clipboard.writeText(userAPIKeyValues.APIKey)}>
          <ContentCopy />
        </IconButton>
      )
    },
    {
      label: "Partner ID",
      value: userAPIKeyValues?.partnerId,
      desc: "Partner ID for either the production or sandbox depending on the environment view selected in the page header.",
      icon: (
        <IconButton color="primary" onClick={() => navigator.clipboard.writeText(userAPIKeyValues.partnerId)}>
          <ContentCopy />
        </IconButton>
      )
    },
    {
      label: "Base Endpoint URL + Version",
      value: userAPIKeyValues?.baseEndpointURL,
      desc: "Base Endpoint URL for either the production or sandbox depending on the environment view selected  in the page header. The API Version will depend on the integration. Refer to the Developer Portal for the latest version.",
      icon: (
        <IconButton color="primary" onClick={() => navigator.clipboard.writeText(userAPIKeyValues?.baseEndpointURL)}>
          <ContentCopy />
        </IconButton>
      )
    },
    {
      label: "Collection URL",
      value: userAPIKeyValues?.APIURL,
      desc: "URL for either the production or sandbox collection depending on the environment view selected  in the page header.",
      icon: (
        <IconButton color="primary" onClick={() => navigator.clipboard.writeText(userAPIKeyValues.APIURL)}>
          <ContentCopy />
        </IconButton>
      )
    }
  ];

  const [matchConfidenceSubmitInProgress, setMatchConfidenceSubmitInProgress] = useState(false);
  const [showNotification, setShowNotification] = useState({ type: "", message: "" });
  const matchConfidenceValues = useSelector(selectMatchConfidenceValues);
  const userData = useSelector(selectUser);
  const epId = userData.selectedPartner.id;

  useEffect(() => {
    setShowNotification(false);
    dispatch(loadApiKeys(epId));
  }, [dispatch, epId]);

  useEffect(() => {
    setShowNotification(false);
    dispatch(loadMatchConfidenceValues(epId));
  }, [dispatch, epId]);

  async function handleMatchConfidenceSubmit() {
    const { epId, ...newMatchConfidenceValues } = matchConfidenceValues;
    setMatchConfidenceSubmitInProgress(true);
    dispatch(loadingPage(true));
    setShowNotification(false);

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await dispatch(submitMatchConfidenceValues(epId, newMatchConfidenceValues));
      setShowNotification({
        type: "success",
        message: "Successfully Updated"
      });
    } catch (error) {
      setMatchConfidenceSubmitInProgress(false);
      setShowNotification({
        type: "error",
        message: "Something went wrong. Please try after some time."
      });
    } finally {
      setMatchConfidenceSubmitInProgress(false);
      dispatch(loadingPage(false));
    }
  }

  async function handleMatchConfidenceReset() {
    setTimeout(function () {
      dispatch(loadMatchConfidenceValues(userData.selectedPartner.id));
    }, 1000);
  }

  function handleSliderChange(rowData) {
    return function (_, value) {
      let changedValue = {};
      changedValue[rowData.slider.valueKey] = value;
      dispatch(updateMatchConfidenceValues(changedValue));
    };
  }

  function handleButtonClick() {
    window.open("https://hubs.ly/Q02cYM880", "_blank");
  }

  function handleAlertClose() {
    setShowNotification(false);
  }
  return (
    <Box>
      <Box sx={sx.headerContent}>
        <Typography variant="h4" sx={sx.heading}>
          {tab === "match-confidence"
            ? I18n.t("matchConfidence.markaazMatchConfidence")
            : tab === "access-administration"
            ? I18n.t("accessAdministration.accessAdministration")
            : I18n.t("contractTerms.contractTerms")}
        </Typography>
        {tab === "contract-terms" ? (
          <MzButton>
            <Typography variant="button2" onClick={handleButtonClick}>
              Master Terms & Conditions
            </Typography>
          </MzButton>
        ) : null}
      </Box>

      <MzTabContext value={tab}>
        <MzTabPanel value="match-confidence">
          <MatchConfidenceMain
            values={matchConfidenceValues}
            data={matchConfidenceData}
            submitInProgress={matchConfidenceSubmitInProgress}
            showNotification={showNotification}
            onAlertClose={handleAlertClose}
            onSliderChange={handleSliderChange}
            onSubmit={handleMatchConfidenceSubmit}
            onReset={handleMatchConfidenceReset}
            isPartnerChanged={!userData.selectedPartner.id}
          />
        </MzTabPanel>
        <MzTabPanel value="access-administration">
          <AdministrationMain dataList={dataList} />
        </MzTabPanel>
        <MzTabPanel value="contract-terms">
          <ContractTermsPage />
        </MzTabPanel>
      </MzTabContext>
    </Box>
  );
};

export default AccessAdministration;
